// ── What "has a funded term" means, in one place ────────────────────────────
//
// Several screens need to show only the borrowers who have actually funded, and
// the temptation each time is to reach for whichever nearby number looks like it
// would do. It will not:
//
//   fundedtermfundings > 0   is a SUM of funding amounts, not a count of funded
//                            terms. A revolver whose opening term funded at zero
//                            and drew afterwards has a funded term and a zero
//                            sum, so this quietly hides a live borrower.
//   accountbalanceeod > 0    hides every paid-off borrower, whose history is
//                            exactly what someone importing bank data may want.
//   ratedloancount > 0       additionally requires a risk rating, which is a
//                            different question entirely.
//
// The right answer is already computed server-side. getAccounts derives
// accountstatus from, among other things:
//
//     WHEN NOT EXISTS (SELECT 1 FROM Terms t JOIN Loans l ON t.loanid = l.id
//                       WHERE l.accountid = a.id AND t.fundingdate IS NOT NULL)
//     THEN 'Funding in Progress'
//
// — which is the predicate itself, negated. 'Terminated' is the other side of
// the same test: it is reached only when there is no funded term AND a
// terminated one, so it is a subset of "never funded" rather than a separate
// case to reason about.
//
// PAID OFF STAYS IN. A borrower who paid off did have funded terms, and their
// bank history is still theirs. Excluding them would be the easy mistake to
// make while writing this and hard to notice afterwards.
export const HAS_NO_FUNDED_TERM = ["Funding in Progress", "Terminated"];

// EXCLUDING TWO STATUSES, NOT LISTING THE GOOD ONES — because the server's
// meaning is "anything that is not one of these two DID fund". A new status
// added to getAccounts later ('Restructured', say) is a funded borrower, and an
// allow-list would silently drop them from every screen that uses this.
//
// BUT A MISSING STATUS IS NOT FUNDED. If accountstatus ever stops arriving — a
// rename, a changed view, a projection someone trimmed — an exclusion test on
// `undefined` matches nothing and the filter degrades into no filter at all,
// passing every borrower including the ones that never closed. That failure is
// invisible. Requiring the field means the opposite failure: the list comes
// back empty and somebody says so within a minute.
export const hasFundedTerm = (account) =>
  !!account
  && typeof account.accountstatus === "string"
  && account.accountstatus !== ""
  && !HAS_NO_FUNDED_TERM.includes(account.accountstatus);
